
import React from 'react';
import PersonalInfoSection from './PersonalInfoSection';
import ContentSection from './ContentSection';
import FormActions from './FormActions';
import { useProductionForm } from '@/hooks/production/useProductionForm';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DirectSupabaseUpload } from '@/components/interview/form/DirectSupabaseUpload';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';

const ProductionForm: React.FC = () => {
  const [isSponsorLogoUploading, setIsSponsorLogoUploading] = React.useState(false);
  const {
    loading,
    answer,
    setAnswer,
    name,
    setName,
    title,
    setTitle,
    company,
    setCompany,
    context,
    setContext,
    contextUrl,
    setContextUrl,
    contextImageUrl,
    setContextImageUrl,
    contextImageCaption,
    setContextImageCaption,
    question,
    setQuestion,
    photoUrl,
    setPhotoUrl,
    photographerCredit,
    setPhotographerCredit,
    tags,
    setTags,
    isSponsored,
    setIsSponsored,
    sponsorCompany,
    setSponsorCompany,
    sponsorLogoUrl,
    setSponsorLogoUrl,
    sponsorLogoPosition,
    setSponsorLogoPosition,
    handleSubmit,
    VALID_TAGS
  } = useProductionForm();

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <PersonalInfoSection 
        name={name}
        setName={setName}
        title={title}
        setTitle={setTitle}
        company={company}
        setCompany={setCompany}
        photoUrl={photoUrl}
        setPhotoUrl={setPhotoUrl}
        photographerCredit={photographerCredit}
        setPhotographerCredit={setPhotographerCredit}
      />
      
      <ContentSection 
        context={context}
        setContext={setContext}
        contextUrl={contextUrl}
        setContextUrl={setContextUrl}
        contextImageUrl={contextImageUrl}
        setContextImageUrl={setContextImageUrl}
        contextImageCaption={contextImageCaption}
        setContextImageCaption={setContextImageCaption}
        question={question}
        setQuestion={setQuestion}
        answer={answer}
        setAnswer={setAnswer}
        tags={tags}
        setTags={setTags}
        validTags={VALID_TAGS}
      />

      {/* Sponsored content section */}
      <div className="border rounded-md p-4">
        <h3 className="text-lg font-semibold mb-4">Annonsørinnhold</h3>
        <div className="space-y-4">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="is-sponsored"
              checked={isSponsored}
              onCheckedChange={(checked) => setIsSponsored(checked === true)}
            />
            <Label htmlFor="is-sponsored" className="cursor-pointer">
              Annonsørinnhold (Betalt Brief)
            </Label>
          </div>
          {isSponsored && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="sponsor-company">Annonsørens firmanavn</Label>
                <Input
                  id="sponsor-company"
                  placeholder="F.eks. Xpeng, DNB, Equinor..."
                  value={sponsorCompany}
                  onChange={(e) => setSponsorCompany(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>
                  Annonsørlogo
                  <span className="text-xs text-muted-foreground font-normal ml-2">
                    (Vises i artikkelen)
                  </span>
                </Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <DirectSupabaseUpload 
                    onImageUploaded={setSponsorLogoUrl} 
                    onUploadingChange={setIsSponsorLogoUploading}
                    initialImage={sponsorLogoUrl || undefined}
                  />
                  <div>
                    {sponsorLogoUrl ? (
                      <div className="w-24 h-24 rounded-full bg-white border overflow-hidden flex items-center justify-center">
                        <img src={sponsorLogoUrl} alt="Sponsor logo" className="w-full h-full object-contain p-2" />
                      </div>
                    ) : (
                      <div className="w-24 h-24 rounded-full bg-muted flex items-center justify-center text-muted-foreground text-xs">
                        Ikke valgt
                      </div>
                    )}
                  </div>
                </div>
                {sponsorLogoUrl && (
                  <div className="mt-3">
                    <Label className="mb-2 block">Plassering av logo</Label>
                    <RadioGroup
                      value={sponsorLogoPosition}
                      onValueChange={(val) => setSponsorLogoPosition(val as 'profile' | 'article' | 'both')}
                      className="flex gap-4"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="profile" id="pos-profile" />
                        <Label htmlFor="pos-profile" className="cursor-pointer font-normal">Ved profilbildet</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="article" id="pos-article" />
                        <Label htmlFor="pos-article" className="cursor-pointer font-normal">Nederst i artikkelen</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="both" id="pos-both" />
                        <Label htmlFor="pos-both" className="cursor-pointer font-normal">Begge steder</Label>
                      </div>
                    </RadioGroup>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      
      <FormActions loading={loading || isSponsorLogoUploading} />
    </form>
  );
};

export default ProductionForm;
